import React from "react";
import { createPortal } from "react-dom";
import { ButtonGroup, Dropdown, Form } from "react-bootstrap";
import type { DropdownMenuProps } from "react-bootstrap";
import { AppType } from "@common/types/sharedTypes";
import { APP_TYPE_LABELS } from "../../helpers/appTypes";

// Renders the dropdown menu into document.body instead of in place.
// react-bootstrap positions the menu with Popper using an inline `transform`
// (or `position: fixed` via popperConfig), but that positioning is still
// clipped/broken if ANY ancestor establishes a containing block (e.g. via
// `transform`, `filter`, `backdrop-filter`, `will-change`, or `contain`) or a
// competing stacking context (e.g. sibling panels with their own z-index).
// Portaling to <body> sidesteps all of that: the menu always positions
// relative to the viewport and stacks above the rest of the app.
const PortalMenu = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ children, ...props }, ref) =>
    createPortal(
      <div ref={ref} {...props}>
        {children}
      </div>,
      document.body,
    ),
);
PortalMenu.displayName = "PortalMenu";

interface ListToolbarFilterDropdownProps {
  sortMode: string;
  setSortMode: (sortMode: string) => void;
  setSortDirection: (direction: "asc" | "desc") => void;
  getType?: (item: any) => any;
  types: string[];
  /** App types the user has unchecked; everything not listed is shown. */
  hiddenAppTypes: ReadonlySet<AppType>;
  toggleAppType: (appType: AppType) => void;
  isInProjectlist?: boolean;
  showActive: boolean;
  setShowActive: React.Dispatch<React.SetStateAction<boolean>>;
  showInactive: boolean;
  setShowInactive: React.Dispatch<React.SetStateAction<boolean>>;
  t: (key: string) => string;
}

// Two of these dropdowns (project list + file list) can be mounted at the
// same time, so the id must not be hardcoded to avoid duplicate DOM ids.
let instanceCounter = 0;

function ListToolbarFilterDropdown({
  sortMode,
  setSortMode,
  setSortDirection,
  getType,
  types,
  hiddenAppTypes,
  toggleAppType,
  isInProjectlist,
  showActive,
  setShowActive,
  showInactive,
  setShowInactive,
  t,
}: ListToolbarFilterDropdownProps) {
  const [instanceId] = React.useState(() => `filter-dropdown-${++instanceCounter}`);

  return (
    <Dropdown id={instanceId} as={ButtonGroup} className="secondary-btn">
      <Dropdown.Toggle
        as="button"
        type="button"
        aria-label={t("buttons.filter")}
        title={t("buttons.filter")}
        style={{ cursor: "pointer", background: "none", border: "none", padding: 0 }}
      >
        <svg fill="#000000" viewBox="-3.2 -3.2 38.40 38.40" xmlns="http://www.w3.org/2000/svg" width="16" height="16">
          <path d="M30 6.749h-28c-0.69 0-1.25 0.56-1.25 1.25s0.56 1.25 1.25 1.25v0h28c0.69 0 1.25-0.56 1.25-1.25s-0.56-1.25-1.25-1.25v0zM24 14.75h-16c-0.69 0-1.25 0.56-1.25 1.25s0.56 1.25 1.25 1.25v0h16c0.69 0 1.25-0.56 1.25-1.25s-0.56-1.25-1.25-1.25v0zM19 22.75h-6.053c-0.69 0-1.25 0.56-1.25 1.25s0.56 1.25 1.25 1.25v0h6.053c0.69 0 1.25-0.56 1.25-1.25s-0.56-1.25-1.25-1.25v0z"></path>
        </svg>
        <span> {t("buttons.filter")}</span>
      </Dropdown.Toggle>

      <Dropdown.Menu
        as={PortalMenu as unknown as React.ComponentType<DropdownMenuProps>}
        align="end"
        className={`filter-dropdown-menu ${isInProjectlist ? "filter-dropdown-menu--project-list" : "filter-dropdown-menu--file-list"}`}
        popperConfig={{ strategy: "fixed" }}
      >
        <Dropdown.Item
          className="d-flex align-items-center"
          active={sortMode === "alphabetical"}
          onClick={() => {
            setSortMode("alphabetical");
            setSortDirection("asc");
          }}
        >
          <Form.Check
            type="checkbox"
            readOnly
            tabIndex={-1}
            checked={sortMode === "alphabetical"}
            className="me-2 m-0 p-0 align-middle"
            style={{ pointerEvents: "none" }}
          />
          <span>{t("filter_and_search_dropdown.sort_alphabetically")}</span>
        </Dropdown.Item>
        <Dropdown.Item
          className="d-flex align-items-center"
          active={sortMode === "latestModified"}
          onClick={() => {
            setSortMode("latestModified");
            setSortDirection("desc");
          }}
        >
          <Form.Check
            type="checkbox"
            readOnly
            tabIndex={-1}
            checked={sortMode === "latestModified"}
            className="me-2 m-0 p-0 align-middle"
            style={{ pointerEvents: "none" }}
          />
          <span>{t("filter_and_search_dropdown.sort_by_last_modified")}</span>
        </Dropdown.Item>

        {getType && types.length > 1 && (
          <>
            {/* Only list app types actually present in this project's files — the user may not
                hold a license for other Simplex apps, so those shouldn't appear as filter options. */}
            {APP_TYPE_LABELS.filter(({ appType }) => types.includes(String(AppType[appType]))).map(
              ({ appType, label }) => (
                <Dropdown.Item
                  key={appType}
                  className="d-flex align-items-center"
                  onClick={() => {
                    toggleAppType(appType);
                  }}
                >
                  <Form.Check
                    type="checkbox"
                    readOnly
                    tabIndex={-1}
                    checked={!hiddenAppTypes.has(appType)}
                    className="me-2 m-0 p-0 align-middle"
                    style={{ pointerEvents: "none" }}
                  />
                  <span>{`Simplex ${label}`}</span>
                </Dropdown.Item>
              ),
            )}
          </>
        )}

        {isInProjectlist && (
          <>
            <Dropdown.Item
              className="d-flex align-items-center"
              onClick={() => {
                setShowActive((prev) => !prev);
              }}
            >
              <Form.Check
                type="checkbox"
                readOnly
                tabIndex={-1}
                checked={showActive}
                className="me-2 m-0 p-0 align-middle"
                style={{ pointerEvents: "none" }}
              />
              <span>
                {t("show")} {t("project.status.active")}
              </span>
            </Dropdown.Item>
            <Dropdown.Item
              className="d-flex align-items-center"
              onClick={() => {
                setShowInactive((prev) => !prev);
              }}
            >
              <Form.Check
                type="checkbox"
                readOnly
                tabIndex={-1}
                checked={showInactive}
                className="me-2 m-0 p-0 align-middle"
                style={{ pointerEvents: "none" }}
              />
              <span>
                {t("show")} {t("project.status.inactive")}
              </span>
            </Dropdown.Item>
          </>
        )}
      </Dropdown.Menu>
    </Dropdown>
  );
}

export default ListToolbarFilterDropdown;
